<template>
    <div class="fullscreen-container mx-auto flex w-full flex-grow">
        <div class="orders-list flex min-w-0 flex-1 bg-white print:hidden">
            <div class="min-w-0 flex-1 bg-white">
                <div>
                    <div
                        class="border-b border-t border-gray-200 p-3"
                        :class="{ 'animate-pulse bg-red-50': connectionError }"
                    >
                        <div class="flex items-start">
                            <div class="flex items-center space-x-3">
                                <span
                                    aria-label="Running"
                                    class="flex h-4 w-4 items-center justify-center rounded-full"
                                    :class="{
                                        animate: isPolling,
                                        'bg-red-100': connectionError,
                                        'bg-green-100': !connectionError
                                    }"
                                >
                                    <span
                                        class="h-2 w-2 rounded-full"
                                        :class="{
                                            'bg-green-400': !connectionError,
                                            'bg-red-400': connectionError
                                        }"
                                    ></span>
                                </span>
                                <div
                                    v-if="connectionError"
                                    class="flex flex-col"
                                >
                                    <h1 class="text-sm font-medium">
                                        {{ $t('liveOrders.connectionError') }}
                                    </h1>
                                    <span class="text-xs text-gray-700">
                                        {{ $t('liveOrders.checkInternet') }}
                                    </span>
                                </div>
                            </div>
                            <div
                                class="relative flex w-full flex-row justify-end"
                                v-if="!connectionError"
                            >
                                <button
                                    @click="openFilterSidebar"
                                    type="button"
                                    class="focus:outline-none inline-flex items-center items-center rounded-md border bg-white px-4 py-2 text-sm font-medium shadow-sm hover:bg-gray-50 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                    :class="
                                        selectedFilters
                                            ? 'border-indigo-600 text-indigo-600'
                                            : 'border-gray-300 text-gray-700'
                                    "
                                >
                                    {{ $t('liveOrders.filters') }}
                                    <span
                                        v-if="selectedFilters"
                                        class="ml-2 h-4 w-4 rounded-full bg-indigo-600 text-xs font-bold text-white"
                                    >
                                        {{ selectedFilters }}
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    class="h-full flex-1 overflow-y-auto pb-36"
                    :class="{ spinner: isLoading }"
                    v-auto-animate
                >
                    <div
                        class="relative z-0 cursor-pointer divide-y divide-gray-200 border-b border-gray-200"
                        v-for="order in orders"
                        :key="order.id"
                        @click="selectOrder(order)"
                    >
                        <LiveOrderListItem
                            :order="order"
                            :class="{
                                'bg-gray-100': selectedOrder.id === order.id
                            }"
                            :table-id="selectedTableId"
                            :selected-venues-count="
                                Array.isArray(venueFilter)
                                    ? venueFilter.length
                                    : 0
                            "
                            :venues-count="
                                Array.isArray(venues) ? venues.length : 0
                            "
                            :is-absolute-time="isAbsoluteTime"
                            @toggle-absolute-time="
                                isAbsoluteTime = !isAbsoluteTime
                            "
                        />
                    </div>
                    <div
                        class="flex h-full items-center justify-center"
                        v-if="!isLoading && !orders.length"
                    >
                        <h5 class="font-bold text-gray-700">
                            {{ $t('liveOrders.noOrdersYet') }}
                        </h5>
                    </div>
                </div>
            </div>
        </div>
        <div
            class="flex w-0 flex-1 flex-col bg-sk-silver-grey lg:shrink-0 lg:border-l lg:border-gray-200 xl:pr-0 print:w-full"
        >
            <div
                v-if="!isWebExtension"
                class="hidden justify-end border-b border-t border-gray-200 bg-white pb-4 pl-4 pr-6 pt-4 sm:flex sm:pl-6 lg:pl-8 xl:border-t-0 xl:pl-6 xl:pt-6 print:hidden"
                style="height: 64px"
            >
                <button
                    class="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-3 text-sm font-medium leading-4 text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    @click="openOperationsSidebar"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        class="-ml-0.5 mr-2 h-5 w-5"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z"
                        />
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                        />
                    </svg>

                    Operations
                </button>
                <button
                    @click="toggleFullScreen"
                    type="button"
                    class="ml-3 focus:outline-none inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-3 text-sm font-medium leading-4 text-gray-700 shadow-sm hover:bg-gray-50 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                    <svg
                        class="-ml-0.5 mr-2 h-4 w-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 11-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12zm-9 7a1 1 0 012 0v1.586l2.293-2.293a1 1 0 111.414 1.414L6.414 15H8a1 1 0 010 2H4a1 1 0 01-1-1v-4zm13-1a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 010-2h1.586l-2.293-2.293a1 1 0 111.414-1.414L15 13.586V12a1 1 0 011-1z"
                            clip-rule="evenodd"
                        ></path>
                    </svg>
                    {{ $t('liveOrders.fullscreen') }}
                </button>
                <router-link
                    :to="{ name: 'dashboard' }"
                    v-if="
                        $store.state.user &&
                        $store.state.user.profile &&
                        $store.state.user.profile.id !== 1828
                    "
                    class="focus:outline-none ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-3 text-sm font-medium leading-4 text-gray-700 shadow-sm hover:bg-gray-50 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                    <svg
                        class="-ml-0.5 mr-2 h-4 w-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z"
                            clip-rule="evenodd"
                        ></path>
                    </svg>
                    {{ $t('liveOrders.exit') }}
                </router-link>
            </div>
            <Order
                v-show="selectedOrder && selectedOrder.id"
                @select="order => (this.selectedOrder = order)"
                class="w-full"
                :key="selectedOrder?.id"
            />
        </div>

        <live-orders-operations-sidebar
            v-model="isOperationSidebarOpen"
            :venues="venues"
        />

        <live-orders-filter-sidebar
            v-model="isFilterSidebarOpen"
            :venues="venues"
            :venueFilter="venueFilter"
            :venueTables="venueTables"
            :showCompleted="showCompleted"
            :showFailed="showFailed"
            @setVenue="setVenue"
            @setTable="setTable"
            @setFulfillmentMethods="setFulfillmentMethods"
            @toggleCompleted="toggleCompleted"
            @toggleFailed="toggleFailed"
            @clearFilters="clearFilters"
        />
    </div>
</template>

<script>
import { Howl, Howler } from 'howler';
import { api as fullscreen } from 'vue-fullscreen';
import qs from 'qs';
import vClickOutside from 'v-click-outside';
import ably from '@/helpers/ably';
import { mapActions, mapGetters } from 'vuex';
import Order from './Order';
import LiveOrderListItem from '@/components/LiveOrderListItem';
import LiveOrdersFilterSidebar from '@/components/sidebars/LiveOrdersFilterSidebar';
import LiveOrdersOperationsSidebar from '@/components/sidebars/LiveOrdersOperationsSidebar.vue';

export default {
    name: 'LiveOrders',
    metaInfo() {
        return {
            title: this.$t('liveOrders.title')
        };
    },
    data() {
        return {
            isLoading: true,
            orders: [],
            venueTables: [],
            ordersLength: 0,
            orderFilter: 'All',
            showOrderFilter: false,
            showRejected: false,
            showCompleted: true,
            showFailed: false,
            showVenueFilter: false,
            venueFilter: '',
            polling: null,
            currentPage: 1,
            pageLimit: 100,
            totalPages: null,
            selectedOrder: {},
            isPolling: false,
            selectedTableId: null,
            connectionError: false,
            isFilterSidebarOpen: false,
            fullscreen: false,
            tableFilter: null,
            fulfillmentMethodFilter: null,
            channels: [],
            lastOrderDate: null,
            lastPreOrderDate: null,
            isAbsoluteTime: false,
            orderSound: null,
            isOperationSidebarOpen: false
        };
    },
    directives: {
        clickOutside: vClickOutside.directive
    },
    components: {
        LiveOrderListItem,
        Order,
        LiveOrdersFilterSidebar,
        LiveOrdersOperationsSidebar
    },
    mounted() {
        ably.connection.on('connected', () => {
            this.connectionError = false;
        });
        ably.connection.on('disconnected', () => {
            this.connectionError = true;
        });
        ably.connection.on('failed', () => {
            this.connectionError = true;
        });
        ably.connection.on('suspended', () => {
            this.connectionError = true;
        });
    },
    watch: {
        currentPage: function () {
            this.fetchOrders(false);
        },
        venueFilter: function (newValue, oldValue) {
            this.venueTables = [];

            if (newValue && newValue.length === 1) {
                this.fetchTables();
            }

            // Normalize values to arrays
            if (!newValue && oldValue) {
                newValue = this.venues.filter(venue => venue.id).map(venue => venue.id);
            }

            if (!oldValue || !oldValue.length) {
                oldValue = [];
                this.unsubscribeAll();
            }

            if (!newValue || !newValue.length) {
                newValue = [];

                if (oldValue && oldValue.length) {
                    this.subscribeAll();
                }
                return;
            }

            // Use Sets for more efficient lookups
            const oldValueSet = new Set(oldValue);
            const newValueSet = new Set(newValue);

            // Calculate venues to unsubscribe (in old but not in new)
            const venuesToUnsubscribe = [...oldValueSet].filter(
                venueId => !newValueSet.has(venueId)
            );

            // Calculate venues to subscribe (in new but not in old)
            const venuesToSubscribe = [...newValueSet].filter(
                venueId => !oldValueSet.has(venueId)
            );

            // Unsubscribe from venues no longer needed
            for (const venueId of venuesToUnsubscribe) {
                this.unsubscribeById(venueId);
            }

            // Subscribe to new venues
            for (const venueId of venuesToSubscribe) {
                this.subscribeById(venueId);
            }
        },
        venues: function () {
            if (!this.venueFilter) {
                this.subscribeAll();
            }
        }
    },
    async created() {
        await this.init();
    },
    beforeDestroy() {
        this.unsubscribeAll();
        if (this.orderSound) {
            this.orderSound.unload();
            this.orderSound = null;
        }
    },
    computed: {
        ...mapGetters({
            isWebExtension: 'ui/isWebExtension',
            venues: 'venues/getVenues',
            accountId: 'user/accountId'
        }),
        selectedFilters() {
            let countSelected = 0;

            if (this.venueFilter) {
                countSelected++;
            }

            if (this.tableFilter) {
                countSelected++;
            }

            if (!this.showCompleted) {
                countSelected++;
            }

            if (this.showFailed) {
                countSelected++;
            }

            return countSelected;
        }
    },
    methods: {
        ...mapActions({
            fetchVenues: 'venues/fetch'
        }),
        clearFilters() {
            this.showCompleted = true;
            this.showFailed = false;
            this.tableFilter = null;
            this.venueFilter = '';

            this.fetchOrders(false, false);
        },
        openFilterSidebar() {
            this.isFilterSidebarOpen = true;
        },
        openOperationsSidebar() {
            this.isOperationSidebarOpen = true;
        },
        selectOrder(order) {
            this.selectedOrder = order;

            if (order.id === this.$route.params.id) {
                return;
            }

            if (order.tableId) {
                this.selectedTableId = order.tableId;
            }

            this.$router.replace({
                name: 'liveOrders',
                params: { id: this.selectedOrder.id }
            });
        },
        venueById(id) {
            return this.venues.find(venue => venue.id === id);
        },
        unsubscribeById(venueId) {
            if (!venueId) {
                return;
            }

            const channelKey = `live-order-${venueId}`;
            if (this.channels[channelKey]) {
                // Unsubscribe from the specific event before detaching
            this.channels[channelKey].unsubscribe(`live-order-event-${venueId}`);

                // Add a small delay before detaching to prevent race conditions
                setTimeout(() => {
              this.channels[channelKey].detach((err) => {
                        if (err) {
                            // eslint-disable-next-line
                  console.warn(`Detach error for venue ${venueId}:`, err);
                        }
                        delete this.channels[channelKey];
                    });
                }, 100);
            }
        },
        unsubscribeAll() {
            for (const venue of this.venues) {
                this.unsubscribeById(venue.id);
            }
        },
        subscribeById(venueId) {
            if (!venueId) {
                return;
            }

            // Check if we already have an active subscription
            if (this.channels[`live-order-${venueId}`]) {
                return;
            }

            const channel = ably.channels.get(`live-order-${venueId}`);

            // Add error handling for the subscription
          channel.subscribe(`live-order-event-${venueId}`, (event) => {
                    this.fetchOrders(event.data === 'order-created', true);
          }, (err) => {
                    if (err) {
                        // eslint-disable-next-line
              console.warn(`Subscription error for venue ${venueId}:`, err);
                    }
          });

            this.channels[`live-order-${venueId}`] = channel;
        },
        subscribeAll() {
            for (const venue of this.venues) {
                this.subscribeById(venue.id);
            }
        },
        async fetchTables() {
            const venueId = parseInt(this.venueFilter[0]);

            const { acceptsInStore } = this.venues.find(
                venue => venue.id === venueId
            );

            if (!acceptsInStore) {
                return;
            }

            try {
                const { data } = await this.$axios.get(
                    `/venues/${venueId}/tables`
                );

                this.venueTables = data;
            } catch (error) {
                throw new Error(`API ${error}`);
            } finally {
                this.isLoading = false;
            }
        },
        async init() {
            this.isLoading = true;

            try {
                // Incipio
                if (this.accountId === '2f8da1f5-7220-4bc9-ae9c-3a184e486932') {
                    this.showCompleted = false;
                }

                if (!this.venues?.length > 0) {
                    await this.fetchVenues();
                }

                if (!this.venues?.length > 0) {
                    return;
                }

                if (!localStorage.getItem('storekit.live-order-venue-filter')) {
                    return;
                }

                const venueId = localStorage.getItem(
                    'storekit.live-order-venue-filter'
                );

                if (venueId.includes(',')) {
                    this.venueFilter = venueId.split(',').map(Number);

                    return;
                }

                if (this.venues.some(venue => venue.id === +venueId)) {
                    this.venueFilter = venueId;

                    return;
                }

                this.subscribeAll();

                localStorage.removeItem('storekit.live-order-venue-filter');
            } catch (error) {
                throw new Error(`API ${error}`);
            } finally {
                this.isLoading = false;
                this.fetchOrders(false, false);
            }
        },
        async fetchOrders(playSound, isPolling) {
            if (this.isLoading || this.isPolling) {
                return;
            }

            if (!isPolling) {
                this.isLoading = true;
            } else {
                this.isPolling = true;
            }

            const params = {
                showRejected: this.showRejected,
                showCompleted: this.showCompleted,
                page: this.currentPage,
                limit: this.pageLimit,
                tableId: this.tableFilter,
                status: this.showFailed ? 'Failed' : null,
                fulfillmentMethods: this.fulfillmentMethodFilter
            };

            if (this.venueFilter) {
                params.venueId = this.venueFilter;
            }

            try {
                const { data } = await this.$axios.get('/live-orders', {
                    params,
                    paramsSerializer: {
                        serialize: params => {
                            return qs.stringify(params, {
                                arrayFormat: 'repeat',
                                skipNulls: true
                            });
                        }
                    }
                });

                if (!data) {
                    return;
                }

                if (playSound && this.newerOrderExist(data.results)) {
                    this.playOrderSound();
                }

                this.orders = data.results;
                this.ordersLength = data.meta?.totalCount || 0;
                this.totalPages = data.meta?.pageCount || 1;

                this.$modal.hide('api-error-modal');
            } catch (error) {
                throw new Error(`API ${error}`);
            } finally {
                this.isLoading = false;
                this.isPolling = false;
            }
        },
        onClickOutside() {
            this.showVenueFilter = false;
        },
        setVenue(val) {
            localStorage.setItem('storekit.live-order-venue-filter', val);
            this.venueFilter = val;
            this.tableFilter = null;

            this.showVenueFilter = false;
            this.fetchOrders();
        },
        setTable(val) {
            this.tableFilter = val;
            this.fetchOrders();
        },
        setFulfillmentMethods(val) {
            this.fulfillmentMethodFilter = val;
            this.fetchOrders();
        },
        toggleCompleted(value) {
            this.showCompleted = !!value;
            this.fetchOrders(false, true);
        },
        toggleFailed(value) {
            this.showFailed = !!value;
            this.showCompleted = !value;
            this.fetchOrders(false, true);
        },
        async toggleFullScreen() {
            await fullscreen.toggle(
                this.$el.querySelector('.fullscreen-container'),
                {
                    teleport: false
                    // callback: isFullscreen => {
                    //     this.fullscreen = isFullscreen;
                    // }
                }
            );
            this.fullscreen = fullscreen.isFullscreen;
        },
        newerOrderExist(orders) {
            if (!orders || orders.length === 0) {
                return false;
            }

            // If this is the first load, initialize dates but don't play sound
            if (!this.lastOrderDate && !this.lastPreOrderDate) {
                this.initializeLastOrderDates(orders);
                return false;
            }

            const currentLastOrderDate = new Date(this.lastOrderDate);
            const currentLastPreOrderDate = new Date(this.lastPreOrderDate);

            let hasNewerOrder = false;
            let newLastOrderDate = currentLastOrderDate;
            let newLastPreOrderDate = currentLastPreOrderDate;

            // Check for newer orders (more efficient than the current implementation)
            for (const order of orders) {
                const orderDate = new Date(order.created_at);

                if (orderDate > currentLastOrderDate) {
                    hasNewerOrder = true;
                    if (orderDate > newLastOrderDate) {
                        newLastOrderDate = orderDate;
                    }
                }

                if (order.preOrderDateTime) {
                    const preOrderDate = new Date(order.preOrderDateTime);
                    if (preOrderDate > currentLastPreOrderDate) {
                        hasNewerOrder = true;
                        if (preOrderDate > newLastPreOrderDate) {
                            newLastPreOrderDate = preOrderDate;
                        }
                    }
                }
            }

            // Update the stored dates
            if (newLastOrderDate > currentLastOrderDate) {
                this.lastOrderDate = newLastOrderDate;
            }
            if (newLastPreOrderDate > currentLastPreOrderDate) {
                this.lastPreOrderDate = newLastPreOrderDate;
            }

            return hasNewerOrder;
        },

        initializeLastOrderDates(orders) {
            let latestOrderDate = null;
            let latestPreOrderDate = null;

            for (const order of orders) {
                const orderDate = new Date(order.created_at);
                if (!latestOrderDate || orderDate > latestOrderDate) {
                    latestOrderDate = orderDate;
                }

                if (order.preOrderDateTime) {
                    const preOrderDate = new Date(order.preOrderDateTime);
                    if (!latestPreOrderDate || preOrderDate > latestPreOrderDate) {
                        latestPreOrderDate = preOrderDate;
                    }
                }
            }

            this.lastOrderDate = latestOrderDate;
            this.lastPreOrderDate = latestPreOrderDate;
        },
        initializeSound() {
            if (this.orderSound) {
                return;
            }

            this.orderSound = new Howl({
                src: [
                    'https://ucarecdn.com/5486f7b7-ebe9-42d3-bea2-852703d95158/newOrder11.mp3',
                    // Fallback sources
                    'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBzuR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmscCj2A0fDEciMFl2+z8+qUQAkUYbXo7KpXFwlBmNztu2gZCUSo3/WuZxMJNnjE6d+UOgQSWKvj8aFLFgc+ltbwtmMcBzuA0PK7bSIFF3fI72Ccgg=='
                ],
                volume: 1.0,
                loop: false,
                preload: true,
                onloaderror: () => {
                    this.$notify({
                        group: 'settings',
                        title: 'Sound cannot be loaded',
                        text: 'Using fallback notification method'
                    });
                },
                onplayerror: () => {
                    this.handleSoundPlayError();
                }
            });
        },

        async playOrderSound() {
            try {
                // Initialize sound if not already done
                this.initializeSound();

                // Check if audio context needs to be resumed (browser autoplay policy)
                if (Howler.ctx && Howler.ctx.state === 'suspended') {
                    await Howler.ctx.resume();
                }

                // Stop any currently playing sound
                if (this.orderSound.playing()) {
                    this.orderSound.stop();
                }

                // Play sound with retry mechanism
                this.playSoundWithRetry();
                
            } catch (error) {
                this.showSoundErrorNotification();
            }
        },

        playSoundWithRetry(attempt = 1, maxAttempts = 3) {
            if (attempt > maxAttempts) {
                this.showSoundErrorNotification();
                return;
            }

            const playId = this.orderSound.play();

            if (playId === null) {
                // Retry after a short delay
                setTimeout(() => {
                    this.playSoundWithRetry(attempt + 1, maxAttempts);
                }, 100 * attempt); // Exponential backoff: 100ms, 200ms, 300ms
                return;
            }

            // Set up success callback
            this.orderSound.once('play', () => {
                    // Sound played successfully
            }, playId);

            // Set up error callback for this specific play instance
            this.orderSound.once('playerror', () => {
                    setTimeout(() => {
                        this.playSoundWithRetry(attempt + 1, maxAttempts);
                    }, 100 * attempt);
            }, playId);
        },

        handleSoundPlayError() {
            // Try to unlock audio context
            this.orderSound.once('unlock', () => {
                this.playOrderSound();
            });

            // Show user-friendly notification
            this.showSoundErrorNotification();
        },

        showSoundErrorNotification() {
            this.$notify({
                group: 'settings',
                title: 'Sound notification unavailable',
                text: 'Please check your browser audio settings or click to enable sound'
            });
        }
    }
};
</script>

<style>
.orders-list {
    max-width: 18rem;
}

@media only screen and (max-width: 768px) {
    .orders-list {
        max-width: 6rem;
    }
}

.animate {
    animation: pulse-green 1s infinite;
}

@keyframes pulse-green {
    0% {
        transform: scale(0.95);
        box-shadow: 0 0 0 0 rgba(51, 217, 178, 0.7);
    }

    70% {
        transform: scale(1);
        box-shadow: 0 0 0 10px rgba(51, 217, 178, 0);
    }

    100% {
        transform: scale(0.95);
        box-shadow: 0 0 0 0 rgba(51, 217, 178, 0);
    }
}
</style>
